import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './context/ThemeContext';
import { isStaff, canAccess, ROLE } from './utils/roles';
import './App.css';

/**
 * Wrap a route element so a wrong-role user is bounced to /admin (which every
 * staff role can see) instead of seeing an empty page that errors on its
 * first API call.
 */
const RoleGuard = ({ role, allowed, children }) =>
  canAccess(role, allowed) ? children : <Navigate to="/admin" replace />;

// Auth Pages
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import EmailVerification from './pages/Auth/EmailVerification';
import VerifyEmailLink from './pages/Auth/VerifyEmailLink';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';

// User Pages
import UserLayout from './pages/User/UserLayoutNew';
import UserDashboard from './pages/User/DashboardNew';
import PortfolioNew from './pages/User/PortfolioNew';
import NotificationsNew from './pages/User/NotificationsNew';
import CommunityNew from './pages/User/CommunityNew';
import SupportNew from './pages/User/SupportNew';
import MessagesNew from './pages/User/MessagesNew';
import NewsNew from './pages/User/NewsNew';
import SettingsNew from './pages/User/SettingsNew';
import MarketsNew from './pages/User/MarketsNew';
import KYC from './pages/User/KYC';
import TradingEnhanced from './pages/User/TradingEnhanced';
import TradingTerminal from './pages/User/TradingTerminal';
import Exchange from './pages/User/Exchange';
import Targets from './pages/User/Targets';
import Orders from './pages/User/Orders';
import BitcoinMining from './pages/User/BitcoinMining';
import BuyCrypto from './pages/User/BuyCrypto';
import ScheduledOrders from './pages/User/ScheduledOrders';
import DepositCrypto from './pages/User/DepositCrypto';
import DepositFiat from './pages/User/DepositFiat';
import WithdrawCrypto from './pages/User/WithdrawCrypto';
import WithdrawFiat from './pages/User/WithdrawFiat';
import CryptoAddresses from './pages/User/CryptoAddresses';
import DepositWithdraw from './pages/User/DepositWithdraw';
import BankAccounts from './pages/User/BankAccounts';
import Markets from './pages/User/Markets';
import CryptoHeatmap from './pages/User/CryptoHeatmap';
import Wallets from './pages/User/Wallets';
import Transactions from './pages/User/Transactions';
import Referrals from './pages/User/Referrals';
import Research from './pages/User/Research';
import Support from './pages/User/Support';
import Profile from './pages/User/Profile';

// Admin Pages
import AdminLayout from './pages/Admin/AdminLayout';
import AdminDashboard from './pages/Admin/Dashboard';
import AdminUsers from './pages/Admin/Users';
import AdminKYC from './pages/Admin/KYC';
import AdminTradeDesk from './pages/Admin/TradeDesk';
import AdminDeposits from './pages/Admin/Deposits';
import AdminWithdrawals from './pages/Admin/Withdrawals';
import AdminTickets from './pages/Admin/Tickets';
import AdminAuditLogs from './pages/Admin/AuditLogs';
import AdminMiningContracts from './pages/Admin/MiningContracts';
import AdminUserWallets from './pages/Admin/AdminUserWallets';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0a0f0d] to-[#1a2f23]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login setUser={setUser} />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth/email-verification" element={<EmailVerification />} />
            <Route path="/auth/verify-email" element={<VerifyEmailLink />} />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />

            {/* User Routes */}
            <Route
              path="/user"
              element={
                user?.role === 'user' ? (
                  <UserLayout user={user} setUser={setUser} />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            >
              <Route index element={<UserDashboard />} />
              <Route path="trading-terminal" element={<Exchange />} />
              <Route path="exchange" element={<Exchange />} />
              <Route path="trading" element={<Navigate to="/user/trading-terminal" replace />} />
              <Route path="targets" element={<Targets />} />
              <Route path="orders" element={<Orders />} />
              <Route path="bitcoin-mining" element={<BitcoinMining />} />
              <Route path="buy-crypto" element={<BuyCrypto />} />
              <Route path="mining" element={<Navigate to="/user/bitcoin-mining" replace />} />
              <Route path="scheduled-orders" element={<ScheduledOrders />} />
              <Route path="deposit-crypto" element={<DepositCrypto />} />
              <Route path="deposit-fiat" element={<DepositFiat />} />
              <Route path="withdraw-crypto" element={<WithdrawCrypto />} />
              <Route path="withdraw-fiat" element={<WithdrawFiat />} />
              <Route path="crypto-addresses" element={<CryptoAddresses />} />
              <Route path="deposit-withdraw" element={<DepositWithdraw />} />
              <Route path="bank-accounts" element={<BankAccounts />} />
              <Route path="markets" element={<MarketsNew />} />
              <Route path="portfolio" element={<PortfolioNew />} />
              <Route path="notifications" element={<NotificationsNew />} />
              <Route path="community" element={<CommunityNew />} />
              <Route path="news" element={<NewsNew />} />
              <Route path="messages" element={<MessagesNew />} />
              <Route path="settings" element={<SettingsNew />} />
              <Route path="crypto-heatmap" element={<CryptoHeatmap />} />
              <Route path="transactions" element={<Transactions />} />
              <Route path="referrals" element={<Referrals />} />
              <Route path="research" element={<Research />} />
              <Route path="wallets" element={<Wallets />} />
              <Route path="support" element={<SupportNew />} />
              <Route path="profile" element={<Profile user={user} />} />
              <Route path="kyc" element={<KYC />} />
            </Route>

            {/* Admin Routes — coarse staff gate on the shell, per-route role
                guards inside. Mirrors backend require_roles(...) — see
                src/utils/roles.js for the allowlists. */}
            <Route
              path="/admin/*"
              element={
                isStaff(user?.role) ? (
                  <AdminLayout user={user} setUser={setUser}>
                    <Routes>
                      <Route index element={<AdminDashboard />} />
                      <Route
                        path="users"
                        element={
                          <RoleGuard
                            role={user.role}
                            allowed={[ROLE.ADMIN, ROLE.SUPER_ADMIN]}
                          >
                            <AdminUsers />
                          </RoleGuard>
                        }
                      />
                      <Route
                        path="users/:userId/wallets"
                        element={
                          <RoleGuard
                            role={user.role}
                            allowed={[ROLE.ADMIN, ROLE.OPERATIONS, ROLE.SUPER_ADMIN]}
                          >
                            <AdminUserWallets />
                          </RoleGuard>
                        }
                      />
                      <Route
                        path="kyc"
                        element={
                          <RoleGuard
                            role={user.role}
                            allowed={[ROLE.COMPLIANCE, ROLE.SUPER_ADMIN]}
                          >
                            <AdminKYC />
                          </RoleGuard>
                        }
                      />
                      <Route
                        path="trade-desk"
                        element={
                          <RoleGuard
                            role={user.role}
                            allowed={[ROLE.TRADE_DESK, ROLE.SUPER_ADMIN]}
                          >
                            <AdminTradeDesk />
                          </RoleGuard>
                        }
                      />
                      <Route
                        path="deposits"
                        element={
                          <RoleGuard
                            role={user.role}
                            allowed={[ROLE.OPERATIONS, ROLE.SUPER_ADMIN]}
                          >
                            <AdminDeposits />
                          </RoleGuard>
                        }
                      />
                      <Route
                        path="withdrawals"
                        element={
                          <RoleGuard
                            role={user.role}
                            allowed={[ROLE.OPERATIONS, ROLE.SUPER_ADMIN]}
                          >
                            <AdminWithdrawals />
                          </RoleGuard>
                        }
                      />
                      <Route
                        path="tickets"
                        element={
                          <RoleGuard
                            role={user.role}
                            allowed={[ROLE.SUPPORT, ROLE.SUPER_ADMIN]}
                          >
                            <AdminTickets />
                          </RoleGuard>
                        }
                      />
                      <Route
                        path="mining"
                        element={
                          <RoleGuard
                            role={user.role}
                            allowed={[ROLE.OPERATIONS, ROLE.SUPER_ADMIN]}
                          >
                            <AdminMiningContracts />
                          </RoleGuard>
                        }
                      />
                      <Route
                        path="audit-logs"
                        element={
                          <RoleGuard
                            role={user.role}
                            allowed={[ROLE.ACCOUNTANT, ROLE.SUPER_ADMIN]}
                          >
                            <AdminAuditLogs />
                          </RoleGuard>
                        }
                      />
                    </Routes>
                  </AdminLayout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Default Route */}
            <Route
              path="/"
              element={
                user ? (
                  user.role === 'user' ? (
                    <Navigate to="/user" replace />
                  ) : (
                    <Navigate to="/admin" replace />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </ThemeProvider>
  );
}

export default App;
